// shared-lib/cabinet-runs/materializeRun.js
//
// ONE materializer for every site (proposal §7). Given a HYDRATED run —
// wall-backed segments carry `.wall`, freestanding ones `_storedIdx`
// (`rehydrateRun` on the web, `resolveSegments` on the server) — and the room
// it stands in, run the fill and return the objects the run emits on THIS
// solve. What differs between a scan reprocess and a web edit is written in
// the POLICY (resolvePolicy.js); nothing here reads `run.source` except the
// `layoutProvenance` stamp, which is provenance.
//
// Pure: never mutates `room` or `run`. Site glue stays with the caller —
// opening hydration (web `openingsForRun`), the panel's read-back stamps
// (`runUppers` / `runFamily`), scan provenance (`addedBy`, `scanAdjusted`).
'use strict';

const { fillRun } = require('./fillRun');
const {
  fillFreestandingSegment,
  defaultSidedness,
  clusterCentresIntoRuns,
} = require('./freestanding');
const { baysForRun } = require('./bays');
const {
  applianceObjectIds,
  isInstallPieceOf,
  splitAppliancePlacements,
  staleInstallIds,
} = require('./applianceInstall');
const { cornerIndexForUnit } = require('./corners');
const { releasesCarriedUpper } = require('./upperCornerTreatment');
const { runAliases } = require('./runIds');
const { runSolidsByOwner } = require('./neighbourRuns');
const { buildSegmentWallResolver } = require('./segmentWall');
const { runCabinetDepth } = require('./runDepth');
const { counterTopOffsetFor } = require('./profiles');
const { isAutoRun } = require('./runSource');
const { isUnknownFamily, familyOf } = require('./families');
const { tooWideFillers } = require('./vendorSizes');
const { profileOf, uppersEnabled } = require('./profiles');
const { fridgeColumnLineY } = require('./fridgeColumn');
const { isOvenColumnPart } = require('./ovenColumn');
const { peninsulaArmBacks } = require('./peninsulaBack');
const { runLinksIn } = require('../group-customization/roomLinks');
const { getFloorY } = require('../vanity-assembly/placementUtils');
const { DEFAULT_CEILING_HEIGHT } = require('../kitchen-assembly/constants');
const { upperBandFor } = require('./upperBand');
const { isPlainFillUpper } = require('../kitchen-assembly/buildUpperCabinet');
const {
  GENERATOR_VERSION,
} = require('../kitchen-assembly/cabinetLayout/schema');
const {
  worldToWallParam,
  getWallAxes,
  wallParamToSurfacePoint,
} = require('../kitchen-assembly/wallMath');
const {
  attachKitchenSinkLinksForGroups,
} = require('../kitchen-assembly/attachKitchenSinkLinks');
const {
  effectiveCountertopAppearance,
  applyCountertopAppearance,
} = require('./countertopAppearance');
const {
  isUnderCabinetLightEligible,
} = require('../under-cabinet-lighting/strips');
const {
  isCrownCarrier,
  isLightRailCarrier,
} = require('../cabinet-trim/carriers');

/** Stamp (or, for `false`, clear) one cabinet trim record on a carrier. */
function stampRunTrim(o, key, record, carries) {
  if (record === undefined || !carries(o)) return;
  if (record) o[key] = { ...record };
  else delete o[key];
}

/** World Y of the room ceiling (mirrors the kitchen generator's getCeilingY).
 *  Falls back to floorY + a standard 8' height when no ceiling geometry. */
function roomCeilingY(room, floorY) {
  const ceiling = (room?.ceilings || [])[0];
  if (ceiling) {
    const y = ceiling.polygonCorners?.[0]?.y;
    if (typeof y === 'number') return y;
    if (typeof ceiling.center?.y === 'number') return ceiling.center.y;
  }
  return floorY + DEFAULT_CEILING_HEIGHT;
}

/**
 * Where a run's uppers hang in `room` — the band the fill resolves
 * (`upperBand.upperBandFor`) from the SAME floor and ceiling `buildFillCtx`
 * hands it, for every caller outside the fill: the panels' Height rows and the
 * height write boundary. Asked of a stored or a hydrated run alike (the band
 * reads only its `upperSpec`, profile and family).
 *
 * @returns {{ bottomY, topY, height, room, requested }}
 */
function runUpperBand(run, room) {
  const floorY = getFloorY(room);
  return upperBandFor(run, { floorY, ceilingY: roomCeilingY(room, floorY) });
}

/**
 * The line a STACKED run's fridge column tops out at in `room`
 * (`fridgeColumn.fridgeColumnLineY`: the stack's top, capped at a vendor's end
 * panel; plan 2026-10-21, C19) — or null for a run with its uppers off or no
 * stacked row, whose column meets its uppers. For a caller outside the fill
 * that re-fits a column (style_kitchen), which must not read the line off the
 * boxes on the wall: the stack's boxes are not the column's line.
 */
function stackedColumnLineY(run, room) {
  if (!run || !uppersEnabled(run)) return null;
  const band = runUpperBand(run, room);
  if (!band.stack) return null;
  return fridgeColumnLineY(run, band, getFloorY(room));
}

// Countertop cosmetics the user edits per-object (not stored in the run spec).
// A re-solve regenerates countertops fresh, so these must be carried across from
// the prior run objects or the user's finish/edge choice is silently lost.
// `edgeProfile` is the front edge; `endFinish` the end treatment (incl.
// waterfall); `options` holds the styling-layer overrides the renderer reads
// FIRST (options.edgeProfile / options.endFinish), so it must survive too.
const COUNTERTOP_EDIT_FIELDS = [
  'solidColor',
  'solidColorFinish',
  'texture',
  'edgeProfile',
  'endFinish',
  'options',
];
const COUNTERTOP_MATCH_EPS2 = 0.05 ** 2; // match by centre within 5cm (squared)

/**
 * Dress freshly materialized countertops: the run's/room's countertop
 * appearance RECORD as the floor, the prior slab's own edits carried over it.
 *
 * Two layers, because they answer different questions. The record says what a
 * countertop in this room is finished in — so a slab the fill rebuilt under a
 * new id, or invented outright, has something to wear. The carry-forward says
 * what the user did to THIS slab, matched by id, else by centre within 5 cm,
 * and it wins wherever it lands: a waterfall end chosen on one island slab is
 * that slab's answer, not the room's.
 *
 * Before the record existed, a slab that matched neither key kept nothing —
 * and both keys move together when a run's START moves, since a slab's id is
 * keyed on wall + startParam and its centre travels half the delta. Dragging a
 * run's left end silently reverted the finish, the edge and the end treatment.
 *
 * A verified no-op on the SCAN site: post-processing writes none of the six
 * fields, so a reprocess has nothing to carry across.
 *
 * @param {Object[]} newObjects     the fill's output
 * @param {Object[]} oldRunObjects  the run's prior objects (the incumbents)
 * @param {Object} [appearance]     `effectiveCountertopAppearance` for the run
 */
function preserveCountertopEdits(newObjects, oldRunObjects, appearance) {
  const oldCts = (oldRunObjects || []).filter((o) => o.type === 'countertop');
  if (!oldCts.length && !appearance) return newObjects;
  const oldById = new Map(oldCts.filter((o) => o.id).map((o) => [o.id, o]));
  return newObjects.map((o) => {
    if (o.type !== 'countertop' || !o.center) return o;
    // The floor, under everything the incumbent carries.
    const dressed = { ...o };
    applyCountertopAppearance(dressed, appearance);
    // By ID first. A slab's id is keyed on the run and the segment index
    // (`<runId>_freestanding_<i>`), so it survives geometry changes — which
    // the centre match below does not: it is a 5 cm window, and the whole
    // point of the island MOVE gesture is to travel further than that. The
    // symptom is a user's waterfall end silently reverting to "match" the
    // first time the island is dragged. The centre match stays as the
    // fallback for slabs whose id the emitter did rebuild.
    let best = o.id ? oldById.get(o.id) : null;
    if (!best) {
      let bestD = COUNTERTOP_MATCH_EPS2;
      for (const oc of oldCts) {
        if (!oc.center) continue;
        const d =
          (oc.center.x - o.center.x) ** 2 + (oc.center.z - o.center.z) ** 2;
        if (d <= bestD) {
          bestD = d;
          best = oc;
        }
      }
    }
    if (!best) return dressed;
    const carried = {};
    for (const f of COUNTERTOP_EDIT_FIELDS) {
      if (best[f] !== undefined) carried[f] = best[f];
    }
    return { ...dressed, ...carried };
  });
}

// Minimum contiguous cabinets for a no-runId cluster to count as a real "run".
const MIN_RUN_SIZE = 3;

/**
 * Group a room's existing lower-cabinets (excluding `excludeRunId`) into real
 * RUNS for the cook-side guess: a runId group (cabinet-run tool) is always a
 * run; cabinets without a runId only count when they form a contiguous counter
 * (≥ MIN_RUN_SIZE in one cluster). Scattered, individually-placed cabinets
 * aren't runs and must not steer an island. Returns an array of centre arrays.
 */
function groupOtherCabinetRuns(room, excludeRunId) {
  const byRunId = new Map();
  const ungrouped = [];
  for (const o of room.objects || []) {
    if (o.type !== 'lower-cabinet' || o.runId === excludeRunId || !o.center)
      continue;
    const centre = { x: o.center.x, z: o.center.z };
    if (o.runId) {
      if (!byRunId.has(o.runId)) byRunId.set(o.runId, []);
      byRunId.get(o.runId).push(centre);
    } else {
      ungrouped.push(centre);
    }
  }
  return [
    ...byRunId.values(),
    ...clusterCentresIntoRuns(ungrouped).filter(
      (c) => c.length >= MIN_RUN_SIZE
    ),
  ];
}

/** The user-facing warnings a frame reconcile produced (wallFrame.js). */
function frameWarningsFor(run) {
  return (run.frameActions || []).map((a) => ({
    severity: 'warning',
    type: 'wall-frame',
    message:
      a.action === 'rebase'
        ? 'This run was rebased onto its wall, which moved since the run was drawn.'
        : 'A wall this run was drawn on is gone or has moved elsewhere; that part of the run was set aside.',
  }));
}

/**
 * The scan's own lower bodies on this run. Strict `runId === run.id` (no
 * aliases) — exactly what the server measured depth from, kept so the
 * snapshot suite stays byte-identical. Deliberately NOT the alias-aware `mine()`
 * the carry-forward uses: that one asks "does this run own it", this one asks
 * "is this what the server measured depth from", and the two are different
 * questions on a corner-joined run.
 */
function measuredBodiesOf(run, objects) {
  return (objects || []).filter(
    (o) => o?.runId === run.id && o.type === 'lower-cabinet'
  );
}

/** Does this run own the object — by its own id, or one of its aliases? */
const ownedBy = (run) => (o) =>
  !!o?.runId && (o.runId === run.id || runAliases(run).includes(o.runId));

/**
 * The corner cabinets — lower and upper — a derived run OWNS in the room. The
 * carry-forward below keeps one verbatim wherever the fill emits no unit of
 * its tier at that vertex, so the fill has to be told they are standing there
 * (`carriedCornerUppers`): ONE reader, because a fill that planned around a
 * different set than the carry keeps would hang uppers through a kept L.
 *
 * Less one: an upper L at a corner the USER has given to something else
 * (`upperCornerTreatment.releasesCarriedUpper`). It is left out HERE, for both
 * readers at once, so it is neither planned around nor kept — the run owns it,
 * nothing re-emits it, and the merge takes it away.
 */
function ownedCornerCabinets(run, room) {
  const mine = ownedBy(run);
  const released = (o) =>
    o.dimensions?.isUpper === true &&
    releasesCarriedUpper(run, cornerIndexForUnit(run, o));
  return (room?.objects || []).filter(
    (o) => o?.type === 'corner-cabinet' && mine(o) && !released(o)
  );
}

/**
 * The engine context for one solve. `ctx` supplies what only the site knows
 * (hydrated openings, the wall list it wants clearance checked against, the
 * other runs' ends, room links); everything else is derived from the room and
 * the policy.
 */
function buildFillCtx(run, room, ctx, policy) {
  const floorY = ctx.floorY ?? getFloorY(room);
  const existingObjects = room?.objects || [];
  const measured = policy.keepMeasuredBodies
    ? measuredBodiesOf(run, existingObjects)
    : [];
  // The depth the bays are BUILT at. The site may name it outright — the
  // minimal-change kitchen re-solves a scanned run at the standard 24" on
  // purpose, which is the one thing `runCabinetDepth` will never answer
  // (it exists to preserve a measured 15.75" run). Otherwise a measured run
  // keeps its measured depth and everything else falls to the builder's own
  // standard.
  // Precedence, most specific first:
  //   1. the SITE said so outright (`ctx.cabinetDepth`) — the minimal-change
  //      kitchen re-solving a scanned run at the standard 24", the one answer
  //      `runCabinetDepth` exists to refuse;
  //   2. the USER chose a depth for this run (`run.depth`, from the profile's
  //      offered list). Above the measured median on purpose: picking 18" on
  //      a run the scan measured at 21.6" has to take effect, or the control
  //      does nothing on exactly the runs that have a measurement;
  //   3. the scan's measured median, for a policy that keeps measured bodies;
  //   4. nothing — `fillRun` falls to the profile's own default.
  // (2) is skipped when the run stores no depth, so every run persisted
  // before this existed resolves exactly as it did.
  const chosenDepth = run?.depth > 0 ? run.depth : null;
  // The counter line this run is BUILT at, threaded like `cabinetDepth` so
  // the opening rules measure a sill against the counter they are about to
  // sit under rather than the kitchen's 36" (`counterTopOffsetFor`).
  const counterTop = counterTopOffsetFor(run);
  const cabinetDepth =
    ctx.cabinetDepth != null
      ? ctx.cabinetDepth
      : (chosenDepth ??
        (policy.keepMeasuredBodies ? runCabinetDepth(measured, run) : null));
  return {
    floorY,
    counterTop,
    // What the OTHER runs in the room stand: an island end butted against one
    // gets no lip into it (`revealsClearOfNeighbours`), and a wide archway's
    // passage is judged with them in it (`adjacentOpeningReservationsForSegment`).
    neighbours: runSolidsByOwner(room, run).neighbours,
    // Ceiling bounds the fridge surround + over-fridge cabinet (and the island
    // hood chimney) an appliance drop emits.
    ceilingY: ctx.ceilingY ?? roomCeilingY(room, floorY),
    ceilingId: room?.ceilings?.[0]?.id || null,
    roomAppearance: room?.cabinetAppearance,
    openings: ctx.openings || [],
    // Every wall the engine may need (alignment-carrying) so it can project
    // openings on ADJACENT walls onto the run's wall for corner clearance. The
    // server hands in the kitchen-clipped list (#1780); the web the raw room
    // walls.
    walls: ctx.walls || buildSegmentWallResolver(room).walls,
    // So a manual run's basin / faucet are REUSED (moved) across re-solves,
    // and the bays' sizes are the appliances' (#1754).
    existingObjects,
    links: ctx.links || runLinksIn(room, run.id),
    // Other runs' terminal points, for `align-end` links (linkAnchors.js).
    // `{}` means "none known", which is what a caller with no other runs
    // passes — never a substitute for a site that has them.
    runEnds: ctx.runEnds || {},
    // A scan run's appliances are MEASURED objects: the corner plan yields to
    // them (a sink gives at most 6" — `sinkSlackToEnd`).
    anchoredAppliances: policy.keepMeasuredBodies,
    // …and a scan run's BAYS are the scan's own: `deriveScanRuns` sized them
    // from these very objects moments earlier, and where a closed run end's
    // reveal was absorbed (`absorbIntoTallUnit`) the bay is deliberately WIDER
    // than the body it names, with `anchorParam` shifted by half the reveal.
    // Re-deriving the footprint from the object shrinks the reservation back
    // while the anchor stays shifted, so the fill packs into a band up to 3"
    // off the physical cabinet while `emitTallUnits` re-seats the incumbent
    // from the STORED bay — the two disagree. Only the footprint pass is
    // skipped: every other reader of `existingObjects` (the basin / faucet
    // reuse, the install reuse, the hood sky) still sees the objects.
    keepStoredBayFootprints: policy.keepMeasuredBodies,
    // Does this run BUILD the fridge column (surround + over-fridge cabinet),
    // or is the one standing there the scan's? A scan run rebuilds neither, so
    // the surround `buildApplianceObjects` emits is dropped below and a scribe
    // beside the fridge must match what the scan measured, not what the install
    // would have built (`fridgeColumnSpec`).
    buildsFridgeColumn: policy.rebuildInstall || policy.emitAppliances,
    // The cabinetry the scan MEASURED on this run: only the adjacent-wall
    // opening reservation reads it, to avoid evicting a body that physically
    // exists (`openings.js`).
    ...(policy.keepMeasuredBodies ? { measuredBodies: measured } : {}),
    // Uppers the run keeps and the engine does not rebuild — a filler grown
    // to match a tall unit has to clear them itself.
    ...(policy.uppers === 'keep'
      ? {
          tallFillerBlockers: existingObjects.filter(
            (o) => o?.type === 'upper-cabinet' && o.center && o.dimensions
          ),
        }
      : {}),
    ...(cabinetDepth != null ? { cabinetDepth } : {}),
    // The upper corner units a derived run already owns. Where the fill hangs
    // no upper L of its own the carry-forward keeps the incumbent — the
    // generator's L can stand on SHORTER legs than the engine's 36" — so the
    // upper tier plans its legs around it (`upperTier.planUpperCorners`).
    // Same gate as the carry-forward itself.
    ...(!policy.emitAppliances
      ? {
          carriedCornerUppers: ownedCornerCabinets(run, room).filter(
            (o) => o.dimensions?.isUpper === true
          ),
        }
      : {}),
  };
}

/**
 * The fill's `appliancePlacements` this solve EMITS: every one on a hand-drawn
 * run (`emitAppliances`), the install half — surround, over-fridge cabinet,
 * hood — on one that rebuilds its install, none on a scan run.
 */
function emittedPlacements(out, policy) {
  if (policy.emitAppliances) return out.appliancePlacements || [];
  if (policy.rebuildInstall)
    return splitAppliancePlacements(out.appliancePlacements).install;
  return [];
}

/**
 * A carried cabinet over a FRIDGE stands where the fridge goes.
 *
 * Carried means verbatim, and the fridge under it does not stay put: a site
 * that moves appliances seats it at its bay's solved centre, and a column at a
 * wall end comes off the wall by the strip its door needs, with a tall filler
 * in it (`spanPacking.seatColumnAtEnd`). Left where the scan found it, the
 * measured cabinet hung over that filler (0D8A06BE, FC5CBAFF: 109 and 122
 * sq in on the first edit of an applied option #1, 2026-10-23). The appliance
 * mover cannot take it along — a run-owned piece is the solve's to place, and
 * a measured over-fridge cabinet names no fixture group — so it is placed
 * here, over the column's solved centre, at its own height and depth.
 *
 * A tall unit's sky is left alone: a tall body is re-seated by the merge's
 * incumbent pass, which may hold it where it stands.
 */
function overFridgeAtSeat(inc, sky, hit, run, out, policy) {
  if (!policy.moveAppliances || sky.kind !== 'fridge') return { ...inc };
  const solved = (out.bays || []).find((b) => b.bayId === sky.bayId);
  const wall = run.segments?.[hit.segIndex]?.wall;
  const axes = wall ? getWallAxes(wall) : null;
  if (!Number.isFinite(solved?.center) || !axes) return { ...inc };
  const delta = solved.center - hit.p;
  if (Math.abs(delta) < 1e-6) return { ...inc };
  return {
    ...inc,
    center: {
      ...inc.center,
      x: inc.center.x + delta * axes.wallDir.x,
      z: inc.center.z + delta * axes.wallDir.z,
    },
  };
}

/**
 * Incumbent cabinetry a DERIVED run owns that THIS fill could not re-emit.
 *
 * Both rescues below are the same shape: the run owns the object, so the merge
 * will sweep it, and the fill produced nothing to take its place — so it rides
 * through verbatim. Lifted out of `materializeRun` unchanged (ledger 70); the
 * incidents each block records are the reason it exists.
 *
 * @param {Object} run    HYDRATED run
 * @param {Object} room   the room BEFORE the solve
 * @param {Object} out    the wall-backed `fillRun` result
 * @param {Object} policy `policyFor(site, run)`
 * @returns {{ objects, carriedIds }} what to append to the run's emitted list,
 *   and the ids of everything carried — the merge must not read one as fresh
 *   output.
 */
function carryForwardOwned(run, room, out, policy) {
  const objects = [];
  // Every incumbent object this solve carried forward VERBATIM rather than
  // re-emitted. The merge (Task 3) must not treat one as fresh.
  const carriedIds = new Set();
  // The appliance objects this run's bays claim, for the carry-forward guard
  // below: once the run rebuilds an install, the incumbent pieces are stale.
  const runApplianceObjects = policy.rebuildInstall
    ? (room?.objects || []).filter(
        (o) => o?.id && applianceObjectIds(run).has(o.id)
      )
    : [];
  // CARRY-FORWARD corners the engine cannot re-emit: the generator has
  // corner VARIANTS (a 24×24 minimal corner beside a crowding range) the
  // run's v1 plan doesn't — it refuses, emits nothing, and since the run
  // OWNS its incumbent corner the re-solve deleted what it couldn't
  // replace (user-seen 2026-08-31, layout 3). An owned corner cabinet at a
  // vertex the fill left empty is kept verbatim, with any owned slab over
  // it.
  if (!policy.emitAppliances) {
    const emittedLower = new Set(
      (out.cornerUnits || []).map((u) => u.cornerIndex)
    );
    const emittedUpper = new Set(
      (out.uppers || [])
        .filter((u) => u.type === 'corner-cabinet')
        .map((u) => cornerIndexForUnit(run, u))
    );
    // A LOWER corner the user has given to something else, and whose pick the
    // fill BUILT: what stands there now is the fill's answer even when that is
    // nothing at all (an empty corner, a blind one on a leg too short for a
    // unit), so the generator's unit is not kept over it. Read off the fill's
    // own report, after the fact — unlike the upper tier's release, nothing
    // had to plan around this unit beforehand.
    const userBuiltLower = new Set(
      (out.lowerCorners || [])
        .filter((c) => c.honoured === true)
        .map((c) => c.cornerIndex)
    );
    const mine = ownedBy(run);
    const carried = [];
    for (const inc of ownedCornerCabinets(run, room)) {
      const ci = cornerIndexForUnit(run, inc);
      if (ci < 0) continue;
      const isUpper = inc.dimensions?.isUpper === true;
      if (!isUpper && userBuiltLower.has(ci)) continue;
      const emitted = isUpper ? emittedUpper : emittedLower;
      if (!emitted.has(ci)) carried.push(inc);
    }
    // The CORNER ids only — a countertop rides on this set to find its
    // parent, and the over-fridge pass below uses it to skip a corner it has
    // already carried. `carriedIds` (the returned one) accumulates
    // everything, corners and slabs alike.
    const carriedCornerIds = new Set(carried.map((c) => c.id));
    for (const ct of (room?.objects || []).filter(
      (o) => o?.type === 'countertop' && mine(o)
    )) {
      if ((ct.parents || []).some((par) => carriedCornerIds.has(par?.id))) {
        carried.push(ct);
      }
    }
    objects.push(
      ...carried.map((c) => {
        carriedIds.add(c.id);
        return { ...c };
      })
    );

    // Same rescue for the OVER-FRIDGE upper (browser follow-up,
    // 2026-08-31): the fill deliberately emits no upper across a
    // fridge/tall span — the surround + over-fridge unit own that sky —
    // so a run-owned over-fridge 'upper-cabinet' was swept on every
    // re-solve and never replaced. Carry any owned upper whose centre
    // sits over a fridge/tall bay that no emitted upper covers.
    //
    // POSITION says which sky an upper stands in; it cannot say whose
    // cabinet it is, and this pass needs the second question. A fridge that
    // MOVES slides under whatever upper was already there, and that upper
    // then answers the positional test exactly as a real over-fridge cabinet
    // does — so it was carried, at its old spot, now standing inside the
    // surround (user-reported 2026-09-07 after dragging a fridge along its
    // run; found on two dev scans nobody had edited, so the drag only made it
    // visible). Geometry cannot separate the two: a GENERATED over-fridge
    // cabinet sits ~27" BELOW its surround's top, because that surround is a
    // full-height box it nests inside, while a MEASURED one sits flush on a
    // surround that stops at the fridge.
    //
    // Provenance can. Only the plain uppers fill stamps
    // `kitchenUpperCabinet_*` (`isPlainFillUpper`), and only that cabinet is
    // one the fill can make again — so not re-emitting it was a DECISION this
    // pass must not override. Everything else over the sky is carried, which
    // is `keepRule`'s own rule: never delete what you cannot rebuild.
    //
    // Not in KEEP mode: there `mergeResolvedRuns` keeps every upper the run
    // owns, so there is nothing to rescue and carrying the incumbent would
    // be a second copy of an object nothing removed. In rebuild/none mode
    // the merge sweeps every owned upper, so one the fill did not re-emit is
    // gone unless it is carried here.
    if (policy.uppers !== 'keep') {
      const applianceSky = baysForRun(run)
        .filter((b) => b.kind === 'fridge' || b.kind === 'tallUnit')
        .map((b) => ({
          bayId: b.id,
          kind: b.kind,
          segIndex: b.segmentIndex ?? 0,
          lo: b.anchorParam - b.width / 2,
          hi: b.anchorParam + b.width / 2,
        }));
      if (applianceSky.length) {
        // The segment an object stands on. `worldToWallParam` projects onto
        // ANY wall and never returns null, so taking the first segment that
        // answered put every object on segment 0 — on a corner-joined run the
        // over-fridge upper stood on segment 2 and this rescue never fired
        // (Anchorage 2 / Scan 1: the upper vanished on every appearance
        // apply). Prefer the wall the object names; else the nearest wall
        // whose span contains the projection.
        const paramOn = (o) => {
          const segs = run.segments || [];
          const named = segs.findIndex(
            (s) => s?.wall && (o.parentWallIds || []).includes(s.wall.id)
          );
          if (named >= 0) {
            return {
              segIndex: named,
              p: worldToWallParam(segs[named].wall, o.center),
            };
          }
          let best = null;
          for (let i = 0; i < segs.length; i += 1) {
            const w = segs[i]?.wall;
            const axes = w ? getWallAxes(w) : null;
            if (!axes) continue;
            const par = worldToWallParam(w, o.center);
            if (par == null || par < 0 || par > axes.wallLen) continue;
            const foot = wallParamToSurfacePoint(w, par);
            if (!foot) continue;
            const off = Math.hypot(o.center.x - foot.x, o.center.z - foot.z);
            if (!best || off < best.off) best = { segIndex: i, p: par, off };
          }
          return best ? { segIndex: best.segIndex, p: best.p } : null;
        };
        // Every upper this solve emits over that sky — the fill's, and the
        // over-fridge cabinet of an install it rebuilds. Reading the fill's
        // alone carried a measured over-fridge cabinet beside the run's own:
        // two over one fridge on the first edit of an applied option #1.
        const emittedUpperSpans = [
          ...(out.uppers || []),
          ...emittedPlacements(out, policy).filter(
            (o) => o?.type === 'upper-cabinet'
          ),
        ]
          .map((u) => {
            const hit = paramOn(u);
            if (!hit) return null;
            const half = (u.dimensions?.x || 0) / 2;
            return {
              segIndex: hit.segIndex,
              lo: hit.p - half,
              hi: hit.p + half,
            };
          })
          .filter(Boolean);
        for (const inc of (room?.objects || []).filter(
          (o) =>
            o?.type === 'upper-cabinet' &&
            mine(o) &&
            !carriedCornerIds.has(o.id) &&
            o.center &&
            // The fill's own product — see the note above.
            !isPlainFillUpper(o) &&
            // An oven column's section is its bay's: the merge's incumbent
            // pass re-seats a claimed one and `keepRule` holds an unclaimed
            // one. Carried as well, it came out twice — two over-oven
            // cabinets in one spot on every re-solve.
            !isOvenColumnPart(o) &&
            // …but NOT an over-fridge cabinet on a run that now rebuilds its
            // own install. This carry-forward exists because the fill could
            // not re-emit one; it can now, and re-adding the incumbent made
            // it "fresh" — so the taller fridge kept growing through the
            // cabinet the rebuild had already decided to drop.
            !(policy.rebuildInstall && isInstallPieceOf(o, runApplianceObjects))
        )) {
          const hit = paramOn(inc);
          if (!hit) continue;
          const sky = applianceSky.find(
            (sk) =>
              sk.segIndex === hit.segIndex && hit.p > sk.lo && hit.p < sk.hi
          );
          if (!sky) continue;
          const covered = emittedUpperSpans.some(
            (sp) =>
              sp.segIndex === hit.segIndex && hit.p > sp.lo && hit.p < sp.hi
          );
          if (!covered) {
            carriedIds.add(inc.id);
            objects.push(overFridgeAtSeat(inc, sky, hit, run, out, policy));
          }
        }
      }
    }
  }
  return { objects, carriedIds };
}

/**
 * The FREE-STANDING half of a run's fill — an island or a peninsula leg, which
 * has no wall behind it and is solved one segment at a time. Lifted out of
 * `materializeRun` unchanged (ledger 70).
 *
 * @param {Object} run    HYDRATED run
 * @param {Object} room   the room BEFORE the solve
 * @param {Object} fillCtx  `buildFillCtx(run, room, ctx, policy)`
 * @param {Object} policy `policyFor(site, run)`
 * @returns {{ objects, warnings, cabinetCount, zones, slidBays }}
 */
function fillFreestandingSegments(run, room, fillCtx, policy) {
  const freeSegs = (run.segments || []).filter((s) => s.freestanding);
  let objects = [];
  let warnings = [];
  let zones = [];
  let slidBays = [];
  let cabinetCount = 0;
  // Free-standing path — one call per segment, with per-segment appliances and
  // overrides reindexed to segmentIndex 0 (each free-standing segment is treated
  // as a single-segment run inside fillFreestandingSegment). The cook-side guess
  // faces the nearest real run (see groupOtherCabinetRuns).
  const otherCabinetRuns = groupOtherCabinetRuns(room, run.id);

  for (const seg of freeSegs) {
    // Fill in `sided` and `cookSide` when the user hasn't set them explicitly.
    const defaults = defaultSidedness(seg, freeSegs, otherCabinetRuns);
    const resolvedSeg = {
      ...seg,
      sided: seg.sided ?? defaults.sided,
      cookSide: seg.cookSide ?? defaults.cookSide,
    };

    // Thread the BAYS and bay overrides that belong to this segment. Each
    // free-standing segment is filled as a single-segment run (segmentIndex 0
    // inside fillFreestandingSegment), so reindex them accordingly. Bays, not
    // the legacy `appliances[]`: on a bays-shaped run the spread `{...run}`
    // used to carry the FULL bay list with original segmentIndexes, so a bay
    // on leg k>0 was neither reserved nor emitted and leg-0 bays leaked into
    // every other leg (F3).
    const segIdx = run.segments.indexOf(seg);
    const segBays = baysForRun(run)
      .filter((b) => (b.segmentIndex ?? 0) === segIdx)
      .map((b) => ({ ...b, segmentIndex: 0 }));
    const segOverrides = (run.bayOverrides || [])
      .filter((o) => o.segmentIndex === segIdx)
      .map((o) => ({ ...o, segmentIndex: 0 }));

    // The stored index embedded by rehydrateRun; falls back to the hydrated index.
    const storedIdx = seg._storedIdx ?? segIdx;

    const result = fillFreestandingSegment(resolvedSeg, {
      floorY: fillCtx.floorY,
      ceilingY: fillCtx.ceilingY,
      ceilingId: fillCtx.ceilingId,
      roomAppearance: fillCtx.roomAppearance,
      openings: [],
      walls: [],
      neighbours: fillCtx.neighbours,
      // Destructure the legacy key away rather than writing `undefined` —
      // this run object can reach a Firestore write on some paths.
      run: (({ appliances: _legacySegAppliances, ...runSansLegacy }) => ({
        ...runSansLegacy,
        bays: segBays,
        bayOverrides: segOverrides,
      }))(run),
      // Unique virtual-wall id per segment → cabinet/countertop/back-panel ids
      // never collide between legs of a multi-segment free-standing run — and
      // run-scoped so two runs' leg 0s don't collide either (object ids are
      // content-keyed now; no timestamp keeps them apart).
      segmentId: `${run.id}_freestanding_${storedIdx}`,
    });
    // Tag every emitted object so the panel knows which stored segment to edit,
    // and surface the segment's depth/sidedness so the panel reads them back
    // after a re-solve (the engine doesn't emit these on individual objects).
    // A DERIVED run (auto / scan) owns its bays and the counter over them, but
    // NOT the appliances: the generator or the scan emitted those and still
    // owns them, so re-emitting doubles them on every re-solve. Same ownership
    // split the wall-backed branch above applies — the free-standing branch
    // could not express it until `fillFreestandingSegment` returned the two
    // groups apart. A manual run owns everything it emits.
    const emitted = policy.emitAppliances
      ? [...(result.objects || []), ...(result.applianceObjects || [])]
      : result.objects || [];
    const taggedObjects = emitted.map((o) => ({
      ...o,
      freeSegIndex: storedIdx,
      depth: resolvedSeg.depth,
      // The per-row split rides along with the total. `depth` alone cannot
      // tell the panel what the cabinets are: the difference between them is
      // the seated edge's knee, and a control that reads only the total can
      // change nothing but the countertop.
      frontDepth: resolvedSeg.frontDepth,
      backDepth: resolvedSeg.backDepth,
      sided: resolvedSeg.sided,
      cookSide: resolvedSeg.cookSide,
      // Whether Side B is seated rides along for the same reason the depths
      // do: the panel's toggle reads its state off the SELECTED object, and
      // without the stamp it would spring back to off after every re-solve.
      seated: resolvedSeg.seated,
    }));
    objects = [...objects, ...taggedObjects];
    if (result.warnings?.length) warnings = [...warnings, ...result.warnings];
    // The leg's zones, under the index its overrides are matched by above.
    zones = [
      ...zones,
      ...(result.zones || []).map((z) => ({ ...z, segIndex: segIdx })),
    ];
    // …and the bays its dishwasher's end piece slid, under the same index.
    slidBays = [
      ...slidBays,
      ...(result.slidBays || []).map((b) => ({ ...b, segIndex: segIdx })),
    ];
    cabinetCount += (result.objects || []).filter(
      (o) => o.type === 'lower-cabinet'
    ).length;
  }

  return { objects, warnings, cabinetCount, zones, slidBays };
}

/**
 * A run of a family this build does not know (`isUnknownFamily`), answered
 * WITHOUT rebuilding it: its own objects back verbatim, every one carried (so
 * the merge keeps them as they are), nothing removed, and a warning saying
 * why. Rebuilding would emit the default family's cabinets under the other
 * family's name and save them — what a pre-Venus build did to a Venus kitchen
 * (2026-10-08). Choosing a family this build DOES know is still a rebuild
 * (`applyRunFamily` sets it before re-solving), so the run can be repaired here.
 */
function leftAsItIs(run, room) {
  const objects = (room?.objects || []).filter((o) => o?.runId === run.id);
  return {
    objects,
    cabinetCount: objects.filter((o) => o.type === 'lower-cabinet').length,
    cornerUnitCount: objects.filter((o) => o.type === 'corner-cabinet').length,
    warnings: [
      {
        type: 'unknown-family',
        family: run.family,
        message: `This run's cabinet line ("${run.family}") isn't in this version of the app, so it was left as it is`,
      },
    ],
    bays: [],
    unsatisfied: [],
    upperCorners: [],
    lowerCorners: [],
    run,
    removedObjectIds: [],
    carriedIds: new Set(objects.map((o) => o.id)),
  };
}

/**
 * A filler wider than the run's vendor sells (2026-10-09): Venus fillers are
 * 3" and 6" stock, and nothing it makes fits a 6–9" gap. The user drew the
 * run, so it is built and priced as several sticks — and says so, in the
 * resolver's own sentence. Silent for a generic family.
 */
function vendorFillerWarnings(run, objects) {
  // One sentence per gap: the base filler and the upper above it say the same.
  const reasons = new Set(
    tooWideFillers(familyOf(run), objects, {
      profileId: profileOf(run).id,
    }).map((f) => f.reason)
  );
  return [...reasons].map((reason) => ({
    severity: 'warning',
    type: 'vendor-filler-too-wide',
    message: `${reason} — move a neighbour to close it, or price it as is.`,
  }));
}

/**
 * Run the engine and flatten every emitted object into the room.objects shape.
 * Sink bases live in `cabinets` (category.sinkBase); DW/range/fridge live in
 * `appliancePlacements`. `ctx.openings` (hydrated by the caller) feed
 * door/window clearance — base cabinets skip floor-reaching openings on the
 * run's walls.
 *
 * @param {Object} run    HYDRATED run
 * @param {Object} room   room doc: walls, floors, ceilings, objects, links,
 *   cabinetAppearance, cabinetRuns
 * @param {Object} ctx    { openings?, walls?, runEnds?, links?, floorY?, ceilingY? }
 * @param {Object} policy `policyFor(site, run)`
 * @returns {{ objects, cabinetCount, cornerUnitCount, warnings, bays,
 *   unsatisfied, run, removedObjectIds, carriedIds }}
 */
function materializeRun(run, room, ctx = {}, policy) {
  if (!policy) throw new Error('materializeRun: a policy is required');
  if (isUnknownFamily(run)) return leftAsItIs(run, room);
  const fillCtx = buildFillCtx(run, room, ctx, policy);

  // Partition segments: wall-backed here, free-standing (island/peninsula) in
  // `fillFreestandingSegments`, which takes the same run and filters its own.
  const wallSegs = (run.segments || []).filter((s) => !s.freestanding);

  let objects = [];
  let cabinetCount = 0;
  let cornerUnitCount = 0;
  let warnings = [];
  let bays = [];
  let unsatisfied = [];
  let zones = [];
  let upperCorners = [];
  let lowerCorners = [];
  // Every incumbent object this solve carried forward VERBATIM rather than
  // re-emitted (`carryForwardOwned`). The merge (Task 3) must not treat one as
  // fresh output. Stays empty for a run with no wall-backed segments.
  let carriedIds = new Set();
  // The back pieces an arm stood in before the engine built them, which this
  // solve's own supersede (`peninsulaArmBacks`).
  let armBackSuperseded = [];

  // Wall-backed path — skipped entirely when the run has only free-standing
  // segments.
  if (wallSegs.length > 0) {
    const wallRun = {
      ...run,
      segments: wallSegs,
      // The engine reads the uppers switch off the RUN; the policy decides.
      uppers: policy.uppers === 'rebuild',
    };
    const out = fillRun(wallRun, fillCtx);
    bays = out.bays || [];
    unsatisfied = out.unsatisfied || [];
    zones = out.zones || [];
    upperCorners = out.upperCorners || [];
    lowerCorners = out.lowerCorners || [];
    cornerUnitCount = (out.cornerUnits || []).length;
    // A DERIVED run (auto / scan) owns the lower/upper cabinet bays + fillers it
    // re-solves, the countertops over them, and — since corner-joined runs
    // (#1494) — its CORNER UNITS: `assignRunIds` owns the incumbent corner, so
    // the re-solve deletes it and must re-emit its own (omitting it here left
    // an empty corner after every re-solve, user-seen 2026-08-31). Appliance
    // objects stay with whoever emitted them — their ANCHORS still shape the
    // packing, we just don't re-emit their objects, so an edit can't
    // double-emit them. Manual runs own everything they emit.
    //
    // Countertops are owned because they must FOLLOW the bays: leaving them
    // behind made the run-wide countertop finish edit a silent no-op on auto
    // kitchens (it keys on `countertop.runId`) and left a slab sized to the
    // pre-edit run. `assignRunIds` / `objectsAfterRunResolve` tag the incumbent
    // slabs so the re-solve replaces rather than duplicates them.
    // A GENERATED run's appliance INSTALL — the fridge surround, the
    // over-fridge cabinet, the range hood — is cabinetry the run builds from
    // the bay's footprint, which since #1754 is the appliance OBJECT's own
    // size. It rides in `appliancePlacements` beside the appliance itself, and
    // dropping the whole list left a 31" surround wrapped around a swapped-in
    // 50" fridge with an over-fridge cabinet the taller box grew 15" through
    // (user-found 2026-09-05 on Golden West / Scan 1). The APPLIANCE object is
    // still never re-emitted here. A SCAN run is excluded: its surround was
    // MEASURED, and rebuilding it would throw away the scan's own record.
    objects = [
      ...out.cabinets,
      ...out.fillers,
      ...out.uppers,
      ...out.cornerUnits,
      ...out.countertops,
      // the microwave in each microwave cabinet: the run's own, on every
      // kind of run, like the cabinet it stands in
      ...(out.microwaves || []),
      ...emittedPlacements(out, policy),
    ];
    // Everything the run OWNS that this fill could not re-emit rides through
    // here: a corner variant the plan refused, an over-fridge upper.
    const carried = carryForwardOwned(run, room, out, policy);
    objects.push(...carried.objects);
    carriedIds = carried.carriedIds;
    // A peninsula arm's back — its panel and the counter strip behind a range
    // — is the run's, built with the arm (`peninsulaBack.js`).
    const backs = peninsulaArmBacks({
      run,
      segments: wallSegs,
      out,
      floorY: fillCtx.floorY,
      roomObjects: room?.objects,
    });
    objects.push(...backs.objects);
    armBackSuperseded = backs.supersededIds;
    cabinetCount = out.cabinets.length;
    warnings = out.warnings || [];
  }
  warnings = [...frameWarningsFor(run), ...warnings];

  // Free-standing path — the island / peninsula legs, one call per segment.
  const free = fillFreestandingSegments(run, room, fillCtx, policy);
  objects = [...objects, ...free.objects];
  zones = [...zones, ...free.zones];
  warnings = [
    ...warnings,
    ...free.warnings,
    ...vendorFillerWarnings(run, objects),
  ];
  cabinetCount += free.cabinetCount;
  // Attribute every object to the run. The engine tags cabinets/corners/
  // appliances, but NOT countertops — without this, re-materializing a run
  // (appliance drop / pin) that replaces objects by runId would leave the old
  // countertop behind and accumulate duplicates.
  for (const o of objects) {
    o.runId = run.id;
    // The run's under-cabinet light record (`run.underCabinetLight`, written
    // by the panel toggle): a re-solve re-emits plain fill uppers FRESH, so a
    // flag that lived only on the old objects would go out with them on the
    // first wall drag. The run is what survives, so the run re-stamps its
    // uppers — every one the strips could light, by the strips' own rule.
    // Unset = say nothing (an upper keeps whatever it carries).
    if (
      typeof run.underCabinetLight === 'boolean' &&
      isUnderCabinetLightEligible(o)
    ) {
      o.underCabinetLight = run.underCabinetLight;
    }
    // The run's cabinet trim (`run.cabinetCrown` / `run.lightRail`, written by
    // the panel's trim rows), re-stamped for the same reason: a record on the
    // run, a stamp on each cabinet that carries it, by the trim's own rule.
    // `false` = off (no key); unset = say nothing.
    stampRunTrim(o, 'cabinetCrown', run.cabinetCrown, isCrownCarrier);
    stampRunTrim(o, 'lightRail', run.lightRail, isLightRailCarrier);
    // Stamp provenance so scoped regeneration can tell edited runs from pristine
    // generator output (regen-merge policy). The edit path sets run.userEdited;
    // a regen re-solve of an unedited run stays 'auto'. Only Phase B output
    // carries this — a scan-derived run was never generator output.
    if (isAutoRun(run)) {
      o.layoutProvenance = {
        layoutVersion: GENERATOR_VERSION,
        sourceKey: run.userEdited ? 'userEdited' : 'auto',
      };
    }
  }
  // Carry the user's countertop finish/edge edits across the re-solve — they
  // live on room.objects, not the run spec, so a fresh materialize drops them.
  const preserved = preserveCountertopEdits(
    objects,
    (room?.objects || []).filter((o) => o.runId === run.id),
    effectiveCountertopAppearance({ run, room })
  );
  // A MANUAL run builds its own sink basin + faucet (a derived run leaves
  // appliances to whoever emitted them), so its sink group gets the same
  // sink → faucet link every other builder writes — born verified. Without
  // this the group opened empty in the Group Editor.
  const linked = policy.emitAppliances
    ? attachKitchenSinkLinksForGroups(
        preserved,
        new Set(preserved.map((o) => o.fixtureGroupId).filter(Boolean))
      )
    : preserved;
  return {
    objects: linked,
    cabinetCount,
    cornerUnitCount,
    warnings,
    // The solved appliance-bay table and the packer's displacements. A caller
    // that persists the re-solve moves the run's appliances here
    // (`moveRunAppliancesToBays`); a derived run never re-emits them.
    bays,
    // A free-standing leg's share of that table: only the bays its
    // dishwasher's end piece SLID, each carrying the leg's virtual `wall`
    // (a free segment has none of its own). The same mover reads it.
    freeSlidBays: free.slidBays,
    unsatisfied,
    // Each segment's fill zone and the hard pieces packed around in it — the
    // stretch a bay-edge drag may grow into (`bayReach.js`).
    zones,
    // What the upper tier decided at each corner and which answers fit there
    // (`upperCornerTreatment.js`) — the corner control's options.
    upperCorners,
    lowerCorners,
    run,
    // Incumbent install pieces this re-solve SUPERSEDED: the old surround now
    // living under a different id, and an over-fridge cabinet that no longer
    // fits above a taller fridge. Nothing else deletes them — the surround
    // carries no `runId`, so `mergeResolvedRuns` keeps it forever — and a
    // superseded twin left in place is exactly the 31"-around-a-50" fridge
    // the rebuild is here to fix (#1754).
    // …but only when this solve actually PRODUCED something. A run whose
    // segments fail to resolve emits nothing, and reporting its incumbent
    // install pieces as superseded would delete a hood and surround that
    // nothing is replacing.
    // …and only when this solve EMITS install pieces. An auto run rebuilds its
    // install from the bay footprint (#1754) and a hand-drawn run emits its own
    // through `appliancePlacements`, so either can supersede an incumbent piece.
    // A scan run emits none — its surround was MEASURED — so nothing of its can
    // be stale, and reporting one would open a delete channel with no
    // replacement behind it.
    // …and an arm's back pieces from before the engine built them.
    removedObjectIds: [
      ...((policy.rebuildInstall || policy.emitAppliances) && linked.length
        ? staleInstallIds(room?.objects, run, linked)
        : []),
      ...armBackSuperseded,
    ],
    carriedIds,
  };
}

module.exports = {
  materializeRun,
  roomCeilingY,
  runUpperBand,
  stackedColumnLineY,
  preserveCountertopEdits,
  groupOtherCabinetRuns,
  buildFillCtx,
};
